import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { CONSENT_KEY, CONSENT_OPEN_EVENT, readChoice } from '../lib/analytics';

// Its own chunk: the first screen's budget (150 kB) doesn't pay for it, and a visitor who has
// already chosen never downloads it unless they open "Cookie settings".
const ConsentBanner = lazy(() => import('./consent/ConsentBanner'));

type State = { open: false } | { open: true; reopened: boolean };

/**
 * Where the cookie banner goes (the top of the column): up while this browser has no usable
 * choice (lib/analytics/consent.ts), and again whenever "Cookie settings" is used.
 */
export function ConsentSlot() {
  const [state, setState] = useState<State>(() =>
    readChoice() ? { open: false } : { open: true, reopened: false },
  );
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onOpen = () => {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setState({ open: true, reopened: true });
    };
    // A choice made in another tab (or on the static site there) closes a first-visit banner.
    const onStorage = (e: StorageEvent) => {
      if (e.key === CONSENT_KEY)
        setState((s) => (s.open && !s.reopened && readChoice() ? { open: false } : s));
    };
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const done = (hadFocus: boolean) => {
    setState({ open: false });
    const back = opener.current;
    opener.current = null;
    if (back?.isConnected) back.focus();
    else if (hadFocus) {
      const main = document.querySelector<HTMLElement>('main');
      if (main) {
        main.tabIndex = -1;
        main.focus();
      }
    }
  };

  if (!state.open) return null;
  return (
    <Suspense fallback={null}>
      <ConsentBanner reopened={state.reopened} onDone={done} />
    </Suspense>
  );
}
